/**
 * Scheduled audit-program clocks. No register entry sets a numeric
 * statutory audit cadence — TARGET, the provider's own audit schedule.
 *
 * Dates are date-only (`YYYY-MM-DD`) strings, matching the
 * `audit_schedules.scheduled_date` column and the cron's existing
 * `todayLocal()` anchor. This function does not itself resolve "today" —
 * the caller supplies it so date-only arithmetic stays anchored exactly as
 * the cron's does today (UTC-midnight of the date string on both sides).
 */
import type { ComplianceDeadlineItem } from './types';
import type { ComplianceDeadlineSettings } from '../compliance-settings';
import { formatAuditTypeLabel } from '../audit-program-scope';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const PROVISION = 'Statura operational target (provider audit-program schedule)';

export interface ScheduledAuditRow {
  id: string;
  auditType: string | null;
  scheduledDate: string;
}

/** Approaching: scheduled within the next 7 days (inclusive of today). */
export function evaluateScheduledAuditApproaching(
  row: ScheduledAuditRow,
  todayStr: string,
  settings?: ComplianceDeadlineSettings,
): { item: ComplianceDeadlineItem; daysRemaining: number } {
  const daysRemaining = Math.max(
    0,
    Math.round(
      (new Date(row.scheduledDate).getTime() - new Date(`${todayStr}T00:00:00.000Z`).getTime()) /
        MS_PER_DAY,
    ),
  );
  return {
    daysRemaining,
    item: {
      id: `audit-schedule:${row.id}`,
      register: 'AUDIT_PROGRAM',
      recordRef: row.id,
      title: `Scheduled audit approaching${row.auditType ? ` (${formatAuditTypeLabel(row.auditType)})` : ''}`,
      dueAt: row.scheduledDate,
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      // Keep the legacy output byte-identical unless a setting was supplied.
      status:
        settings?.audit_schedule_warning_lead !== undefined &&
        settings.audit_schedule_warning_lead !== 7 &&
        daysRemaining > settings.audit_schedule_warning_lead
          ? 'UPCOMING'
          : 'DUE_SOON',
      href: '/compliance/quality',
      meta: { daysRemaining },
    },
  };
}

/** Overdue: scheduled date has passed with the audit still SCHEDULED. */
export function evaluateScheduledAuditOverdue(
  row: ScheduledAuditRow,
  todayStr: string,
): { item: ComplianceDeadlineItem; daysOverdue: number } {
  const daysOverdue = Math.max(
    1,
    Math.round(
      (new Date(`${todayStr}T00:00:00.000Z`).getTime() - new Date(row.scheduledDate).getTime()) /
        MS_PER_DAY,
    ),
  );
  return {
    daysOverdue,
    item: {
      id: `audit-schedule-overdue:${row.id}`,
      register: 'AUDIT_PROGRAM',
      recordRef: row.id,
      title: `Scheduled audit overdue${row.auditType ? ` (${formatAuditTypeLabel(row.auditType)})` : ''}`,
      dueAt: row.scheduledDate,
      kind: 'TARGET',
      provisionOrTargetLabel: PROVISION,
      status: 'OVERDUE',
      href: '/compliance/quality',
      meta: { daysOverdue },
    },
  };
}
